import { toISODate } from '@/routes/jobs/helpers';

import type { CalendarCell } from './types';

/** First day of `d`'s month, at local midnight. */
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** First day of the month `n` months from `d` (n may be negative). */
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/**
 * The 6×7 (always 42-cell) grid for `month`'s calendar page: leading days from
 * the previous month, `month`'s own days, then trailing days from the next
 * month. `weekStartsOn` picks the left-most weekday column.
 *
 * `react-big-calendar` renders the month grid itself now; this stays purely to
 * compute the inclusive fetch window (`gridRange`) so recurring jobs can be
 * expanded across every visible day.
 */
export function getCalendarMatrix(
  month: Date,
  weekStartsOn: 0 | 1 = 0,
): CalendarCell[][] {
  const first = startOfMonth(month);
  const lead = (first.getDay() - weekStartsOn + 7) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - lead);

  const todayIso = toISODate(new Date());
  const rows: CalendarCell[][] = [];

  for (let r = 0; r < 6; r += 1) {
    const row: CalendarCell[] = [];
    for (let c = 0; c < 7; c += 1) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + r * 7 + c);
      const iso = toISODate(date);
      row.push({
        date,
        iso,
        inMonth: date.getMonth() === first.getMonth(),
        isToday: iso === todayIso,
      });
    }
    rows.push(row);
  }

  return rows;
}

/** The inclusive ISO date span the grid covers — a ready `listJobs` filter. */
export function gridRange(matrix: CalendarCell[][]): {
  start: string;
  end: string;
} {
  const flat = matrix.flat();
  return { start: flat[0].iso, end: flat[flat.length - 1].iso };
}

/**
 * The inclusive ISO window RBC's agenda view shows: `[date, date + length)`,
 * i.e. `length` days starting at `date`. Matches the `agendaLength` prop so the
 * fetch window and what's rendered agree.
 */
export function agendaRange(
  date: Date,
  length = 30,
): { start: string; end: string } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + length - 1);
  return { start: toISODate(start), end: toISODate(end) };
}
